import { useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback } from 'react';

import { fetchMerchantVenuesV3, fetchMerchantVenuesV4 } from 'apis/restaurant-api/merchant-admin/venue/api';
import { MerchantVenuesResponse } from 'apis/restaurant-api/merchant-admin/venue/types';

import { useIsMerchantVenuesV4ApiEnabled } from './useIsMerchantVenuesV4ApiEnabled';

const VENUE_PAGE_SIZE_V3 = 300;
const VENUE_PAGE_SIZE_V4 = 100;

const useMerchantVenues = (isEnabled: boolean, merchantId: string, pageSize?: number) => {
  const { isMerchantVenuesV4ApiEnabled, isLoadingIsMerchantVenuesV4ApiEnabled } =
    useIsMerchantVenuesV4ApiEnabled(isEnabled);

  const queryFn = useCallback(
    ({ pageParam: pageToken = '' }) => {
      if (isMerchantVenuesV4ApiEnabled) {
        return fetchMerchantVenuesV4(merchantId, pageSize ?? VENUE_PAGE_SIZE_V4, pageToken);
      }
      return fetchMerchantVenuesV3(merchantId, pageSize ?? VENUE_PAGE_SIZE_V3, pageToken);
    },
    [merchantId, pageSize, isMerchantVenuesV4ApiEnabled],
  );

  const queryKey = [isMerchantVenuesV4ApiEnabled ? '/v4' : '/v3', '/merchant-admin/merchants/', merchantId, '/venues'];

  const { data, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
    MerchantVenuesResponse,
    AxiosError
  >({
    queryFn,
    queryKey,
    getNextPageParam: (lastPage: MerchantVenuesResponse) => lastPage.next_page_token,
    enabled: isEnabled && !!merchantId && !isLoadingIsMerchantVenuesV4ApiEnabled,
    refetchOnWindowFocus: false,
  });

  const isLoadingMerchantVenues = isLoadingIsMerchantVenuesV4ApiEnabled || (isEnabled && isLoading);

  return { data, error, isLoading: isLoadingMerchantVenues, fetchNextPage, hasNextPage, isFetchingNextPage };
};

export default useMerchantVenues;
